/** 用户界面偏好（按登录用户 ID 存 localStorage） */
export type TablePageSizeKey =
  | 'projectDashboard.componentRecommend'
  | 'projectDashboard.bom'
  | 'projectDashboard.issues'
  | 'projectRequirementLedger'
  | 'aiRequirementDetail';

export type TableColumnWidthKey =
  | 'engineering.requirementLedger'
  | 'engineering.componentRecommend'
  | 'engineering.bom3d'
  | 'engineering.bom2d'
  | 'engineering.bomUnified'
  | 'engineering.issueList'
  | 'aiRequirementDetail';

export type TableRowHeightKey =
  | 'engineering.requirementLedger'
  | 'engineering.componentRecommend'
  | 'engineering.bomUnified';

/** WBS 任务详情未保存草稿（按 taskId 存） */
export type WbsTaskEditDraft = {
  description?: string;
  parentTaskId?: number | null;
  assigneeUserIds?: number[];
  ownerUserId?: number | null;
  startDate?: string;
  dueDate?: string;
  workload?: string;
  status?: string;
  appealReason?: string;
  wbsPhaseCode?: string;
  savedAt?: number;
  /**
   * 起草时服务端可编辑字段快照；若当前服务端快照与此不同，说明他人已改过，草稿应淹没。
   */
  baseSnapshot?: string;
};

/** 文档评审未保存草稿元数据（正文在独立 localStorage draft key） */
export type DocReviewEditDraftMeta = {
  objectKey: string;
  /** 草稿所属历史版本 id */
  versionId: string;
  savedAt?: number;
  /** 起草时该版本的 updatedAt；他人覆盖同版本后用于淹没草稿 */
  baseVersionUpdatedAt?: number;
  /** 起草时共享工作区 revision */
  baseRevision?: number;
};

/** WBS 任务计划侧栏筛选（按项目） */
export type WbsTaskPlanFilters = {
  keyword?: string;
  phase?: string;
  status?: string;
  /** 0 / null / undefined = 全部 */
  assigneeUserId?: number | null;
};

/** 问题清单筛选（按项目） */
export type IssueListFilters = {
  status?: string;
  risk?: string;
  category?: string;
  /** '' / undefined = 全部 */
  handlerUserId?: number | '';
  keyword?: string;
  deadlineStart?: string;
  deadlineEnd?: string;
  sort?: string;
};

/** 问题统计弹框筛选（个人全局） */
export type IssueOverviewFilters = {
  severity?: string;
  status?: string;
  creator?: string;
  handlerUserId?: number | '';
  resolvedDate?: string | null;
  mineSeverity?: string;
  mineResolvedDate?: string | null;
};

export type UserUiPreferences = {
  tablePageSize?: Partial<Record<TablePageSizeKey, number>>;
  tableColumnWidths?: Partial<Record<TableColumnWidthKey, Record<string, number>>>;
  /**
   * 工程表按行行高（px）。
   * - 新格式：Record<rowKey, heightPx>
   * - 旧格式 number：已废弃，读取时忽略
   */
  tableRowHeights?: Partial<Record<TableRowHeightKey, number | Record<string, number>>>;
  /** WBS 任务模式左侧栏宽度（px） */
  wbsSidebarWidth?: number;
  /** WBS 任务编辑草稿：key = `${projectId}:${taskId}` */
  wbsTaskEditDrafts?: Record<string, WbsTaskEditDraft>;
  /** 文档评审编辑草稿索引：key = `${projectId}:${objectKey}` */
  docReviewEditDrafts?: Record<string, DocReviewEditDraftMeta>;
  /** WBS 任务计划筛选：key = `${projectId}` */
  wbsTaskPlanFilters?: Record<string, WbsTaskPlanFilters>;
  /** 问题清单筛选：key = `${projectId}` */
  issueListFilters?: Record<string, IssueListFilters>;
  /** 问题统计弹框筛选 */
  issueOverviewFilters?: IssueOverviewFilters;
  /** 文档空间「仅看最新版」：key = `${projectId}` */
  documentSpaceLatestOnly?: Record<string, boolean>;
};

export const WBS_SIDEBAR_WIDTH_DEFAULT = 300;
export const WBS_SIDEBAR_WIDTH_MIN = 240;
export const WBS_SIDEBAR_WIDTH_MAX = 480;

export function clampWbsSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return WBS_SIDEBAR_WIDTH_DEFAULT;
  return Math.min(WBS_SIDEBAR_WIDTH_MAX, Math.max(WBS_SIDEBAR_WIDTH_MIN, Math.round(width)));
}

export function getWbsSidebarWidth(userId: number | string | null | undefined): number {
  if (userId == null || userId === '') return WBS_SIDEBAR_WIDTH_DEFAULT;
  const n = readRaw(userId).wbsSidebarWidth;
  if (typeof n === 'number') return clampWbsSidebarWidth(n);
  return WBS_SIDEBAR_WIDTH_DEFAULT;
}

export function setWbsSidebarWidth(userId: number | string | null | undefined, width: number) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  prefs.wbsSidebarWidth = clampWbsSidebarWidth(width);
  writeRaw(userId, prefs);
}

const STORAGE_PREFIX = 'aipd:user-ui-prefs:v1';

function storageKey(userId: number | string) {
  return `${STORAGE_PREFIX}:${userId}`;
}

function readRaw(userId: number | string): UserUiPreferences {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return typeof parsed === 'object' && parsed != null ? (parsed as UserUiPreferences) : {};
  } catch {
    return {};
  }
}

function writeRaw(userId: number | string, prefs: UserUiPreferences) {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    // ignore quota / private mode
  }
}

export function getTablePageSize(
  userId: number | string | null | undefined,
  key: TablePageSizeKey,
  fallback: number,
  allowed: readonly number[],
): number {
  if (userId == null || userId === '') return fallback;
  const n = readRaw(userId).tablePageSize?.[key];
  if (typeof n === 'number' && allowed.includes(n)) return n;
  return fallback;
}

export function setTablePageSize(
  userId: number | string | null | undefined,
  key: TablePageSizeKey,
  size: number,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  if (!prefs.tablePageSize) prefs.tablePageSize = {};
  prefs.tablePageSize[key] = size;
  writeRaw(userId, prefs);
}

export function getTableColumnWidths(
  userId: number | string | null | undefined,
  key: TableColumnWidthKey,
): Record<string, number> | null {
  if (userId == null || userId === '') return null;
  const saved = readRaw(userId).tableColumnWidths?.[key];
  if (!saved || typeof saved !== 'object') return null;
  return { ...saved };
}

export function setTableColumnWidths(
  userId: number | string | null | undefined,
  key: TableColumnWidthKey,
  widths: Record<string, number>,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  if (!prefs.tableColumnWidths) prefs.tableColumnWidths = {};
  prefs.tableColumnWidths[key] = { ...widths };
  writeRaw(userId, prefs);
}

const ROW_HEIGHT_MIN = 36;
const ROW_HEIGHT_MAX = 200;

function clampStoredRowHeight(n: number, fallback: number): number {
  if (!Number.isFinite(n)) return fallback;
  return Math.min(ROW_HEIGHT_MAX, Math.max(ROW_HEIGHT_MIN, Math.round(n)));
}

function normalizeRowHeightMap(raw: unknown): Record<string, number> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  // 旧版存的是单一 number，迁移时丢弃（改为按行配置）
  if (typeof raw === 'number') return {};
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!k || typeof v !== 'number') continue;
    out[k] = clampStoredRowHeight(v, v);
  }
  return out;
}

/** 读取按行行高映射（个人设置） */
export function getTableRowHeightMap(
  userId: number | string | null | undefined,
  key: TableRowHeightKey,
): Record<string, number> {
  if (userId == null || userId === '') return {};
  return normalizeRowHeightMap(readRaw(userId).tableRowHeights?.[key]);
}

/** 整表写入按行行高映射 */
export function setTableRowHeightMap(
  userId: number | string | null | undefined,
  key: TableRowHeightKey,
  heights: Record<string, number>,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  if (!prefs.tableRowHeights) prefs.tableRowHeights = {};
  prefs.tableRowHeights[key] = normalizeRowHeightMap(heights);
  writeRaw(userId, prefs);
}

/** @deprecated 统一行高已改为按行映射，保留空实现避免旧引用报错 */
export function getTableRowHeight(
  _userId: number | string | null | undefined,
  _key: TableRowHeightKey,
  fallback: number,
): number {
  return fallback;
}

/** @deprecated */
export function setTableRowHeight(
  userId: number | string | null | undefined,
  key: TableRowHeightKey,
  heightPx: number,
) {
  // 不再写入单一行高，避免覆盖按行映射
  void userId;
  void key;
  void heightPx;
}

export function wbsTaskDraftKey(projectId: number, taskId: number) {
  return `${projectId}:${taskId}`;
}

export function getWbsTaskEditDraft(
  userId: number | string | null | undefined,
  projectId: number,
  taskId: number,
): WbsTaskEditDraft | null {
  if (userId == null || userId === '') return null;
  const hit = readRaw(userId).wbsTaskEditDrafts?.[wbsTaskDraftKey(projectId, taskId)];
  if (!hit || typeof hit !== 'object') return null;
  return { ...hit };
}

export function setWbsTaskEditDraft(
  userId: number | string | null | undefined,
  projectId: number,
  taskId: number,
  draft: WbsTaskEditDraft,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  if (!prefs.wbsTaskEditDrafts) prefs.wbsTaskEditDrafts = {};
  prefs.wbsTaskEditDrafts[wbsTaskDraftKey(projectId, taskId)] = {
    ...draft,
    savedAt: Date.now(),
  };
  writeRaw(userId, prefs);
}

export function clearWbsTaskEditDraft(
  userId: number | string | null | undefined,
  projectId: number,
  taskId: number,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  if (!prefs.wbsTaskEditDrafts) return;
  delete prefs.wbsTaskEditDrafts[wbsTaskDraftKey(projectId, taskId)];
  writeRaw(userId, prefs);
}

/** 指定项目下有未保存草稿的任务 ID 列表 */
export function listWbsTaskDraftTaskIds(
  userId: number | string | null | undefined,
  projectId: number,
): number[] {
  if (userId == null || userId === '') return [];
  const drafts = readRaw(userId).wbsTaskEditDrafts;
  if (!drafts) return [];
  const prefix = `${projectId}:`;
  const ids: number[] = [];
  for (const key of Object.keys(drafts)) {
    if (!key.startsWith(prefix)) continue;
    const id = Number(key.slice(prefix.length));
    if (Number.isFinite(id) && id > 0) ids.push(id);
  }
  return ids;
}

/** 服务端可编辑字段相对草稿基线已变化 → 草稿应淹没 */
export function isWbsTaskDraftStale(
  draft: WbsTaskEditDraft | null | undefined,
  serverSnapshot: string,
): boolean {
  if (!draft?.baseSnapshot) return false;
  return draft.baseSnapshot !== serverSnapshot;
}

/**
 * 用当前任务服务端快照淹没过期草稿；任务已删除的草稿一并清理。
 * @returns 仍有效的草稿任务 ID
 */
export function pruneStaleWbsTaskEditDrafts(
  userId: number | string | null | undefined,
  projectId: number,
  serverSnapshotsByTaskId: Record<number, string>,
): number[] {
  if (userId == null || userId === '') return [];
  const prefs = readRaw(userId);
  const drafts = prefs.wbsTaskEditDrafts;
  if (!drafts) return [];
  const prefix = `${projectId}:`;
  let changed = false;
  const remain: number[] = [];
  for (const key of Object.keys(drafts)) {
    if (!key.startsWith(prefix)) continue;
    const id = Number(key.slice(prefix.length));
    if (!(Number.isFinite(id) && id > 0)) {
      delete drafts[key];
      changed = true;
      continue;
    }
    const serverSnap = serverSnapshotsByTaskId[id];
    if (serverSnap == null) {
      // 任务已不在列表中
      delete drafts[key];
      changed = true;
      continue;
    }
    if (isWbsTaskDraftStale(drafts[key], serverSnap)) {
      delete drafts[key];
      changed = true;
      continue;
    }
    remain.push(id);
  }
  if (changed) writeRaw(userId, prefs);
  return remain;
}

function docReviewDraftKey(projectId: number, objectKey: string) {
  return `${projectId}:${objectKey}`;
}

export function getDocReviewEditDraftMeta(
  userId: number | string | null | undefined,
  projectId: number,
  objectKey: string,
): DocReviewEditDraftMeta | null {
  if (userId == null || userId === '' || !objectKey) return null;
  const hit = readRaw(userId).docReviewEditDrafts?.[docReviewDraftKey(projectId, objectKey)];
  if (!hit || typeof hit !== 'object') return null;
  return { ...hit };
}

export function setDocReviewEditDraftMeta(
  userId: number | string | null | undefined,
  projectId: number,
  objectKey: string,
  meta: DocReviewEditDraftMeta,
) {
  if (userId == null || userId === '' || !objectKey) return;
  const prefs = readRaw(userId);
  if (!prefs.docReviewEditDrafts) prefs.docReviewEditDrafts = {};
  prefs.docReviewEditDrafts[docReviewDraftKey(projectId, objectKey)] = {
    ...meta,
    objectKey,
    savedAt: Date.now(),
  };
  writeRaw(userId, prefs);
}

export function clearDocReviewEditDraftMeta(
  userId: number | string | null | undefined,
  projectId: number,
  objectKey: string,
) {
  if (userId == null || userId === '' || !objectKey) return;
  const prefs = readRaw(userId);
  if (!prefs.docReviewEditDrafts) return;
  delete prefs.docReviewEditDrafts[docReviewDraftKey(projectId, objectKey)];
  writeRaw(userId, prefs);
}

/** 指定项目下有未保存文档草稿的 objectKey 列表 */
export function listDocReviewDraftObjectKeys(
  userId: number | string | null | undefined,
  projectId: number,
): string[] {
  if (userId == null || userId === '') return [];
  const drafts = readRaw(userId).docReviewEditDrafts;
  if (!drafts) return [];
  const prefix = `${projectId}:`;
  const keys: string[] = [];
  for (const key of Object.keys(drafts)) {
    if (!key.startsWith(prefix)) continue;
    const objectKey = key.slice(prefix.length);
    if (objectKey) keys.push(objectKey);
  }
  return keys;
}

function projectFilterKey(projectId: number): string {
  return String(projectId);
}

export function getWbsTaskPlanFilters(
  userId: number | string | null | undefined,
  projectId: number,
): WbsTaskPlanFilters {
  if (userId == null || userId === '' || !(projectId > 0)) return {};
  const saved = readRaw(userId).wbsTaskPlanFilters?.[projectFilterKey(projectId)];
  return saved && typeof saved === 'object' ? { ...saved } : {};
}

export function setWbsTaskPlanFilters(
  userId: number | string | null | undefined,
  projectId: number,
  filters: WbsTaskPlanFilters,
) {
  if (userId == null || userId === '' || !(projectId > 0)) return;
  const prefs = readRaw(userId);
  if (!prefs.wbsTaskPlanFilters) prefs.wbsTaskPlanFilters = {};
  prefs.wbsTaskPlanFilters[projectFilterKey(projectId)] = { ...filters };
  writeRaw(userId, prefs);
}

export function getIssueListFilters(
  userId: number | string | null | undefined,
  projectId: number,
): IssueListFilters {
  if (userId == null || userId === '' || !(projectId > 0)) return {};
  const saved = readRaw(userId).issueListFilters?.[projectFilterKey(projectId)];
  return saved && typeof saved === 'object' ? { ...saved } : {};
}

export function setIssueListFilters(
  userId: number | string | null | undefined,
  projectId: number,
  filters: IssueListFilters,
) {
  if (userId == null || userId === '' || !(projectId > 0)) return;
  const prefs = readRaw(userId);
  if (!prefs.issueListFilters) prefs.issueListFilters = {};
  prefs.issueListFilters[projectFilterKey(projectId)] = { ...filters };
  writeRaw(userId, prefs);
}

export function getIssueOverviewFilters(
  userId: number | string | null | undefined,
): IssueOverviewFilters {
  if (userId == null || userId === '') return {};
  const saved = readRaw(userId).issueOverviewFilters;
  return saved && typeof saved === 'object' ? { ...saved } : {};
}

export function setIssueOverviewFilters(
  userId: number | string | null | undefined,
  filters: IssueOverviewFilters,
) {
  if (userId == null || userId === '') return;
  const prefs = readRaw(userId);
  prefs.issueOverviewFilters = { ...filters };
  writeRaw(userId, prefs);
}

/** 文档空间「仅看最新版」，默认 true */
export function getDocumentSpaceLatestOnly(
  userId: number | string | null | undefined,
  projectId: number,
): boolean {
  if (userId == null || userId === '' || !(projectId > 0)) return true;
  const saved = readRaw(userId).documentSpaceLatestOnly?.[projectFilterKey(projectId)];
  return typeof saved === 'boolean' ? saved : true;
}

export function setDocumentSpaceLatestOnly(
  userId: number | string | null | undefined,
  projectId: number,
  latestOnly: boolean,
) {
  if (userId == null || userId === '' || !(projectId > 0)) return;
  const prefs = readRaw(userId);
  if (!prefs.documentSpaceLatestOnly) prefs.documentSpaceLatestOnly = {};
  prefs.documentSpaceLatestOnly[projectFilterKey(projectId)] = !!latestOnly;
  writeRaw(userId, prefs);
}
